// Encuadre · cuánto margen liso tiene una foto arriba y abajo.
//
// 📄 El tratamiento entero, con lo probado y cómo probarlo en local:
// `docs/ENCUADRE_DE_FOTOS.md`.
//
// Pedido y aprobado por Pablo el 21.10.26 para la franja de conciertos
// (`HeroAgenda`): las fotos de Ticketmaster traen a menudo mucho fondo liso
// arriba (la pared del estudio, un negro) y, ancladas arriba, el contenido cae
// bajo los datos. Con esto se sabe dónde empieza y acaba lo que hay en la foto,
// y quien la pinta la coloca para centrar eso en su visor. Lo usan la franja y
// la foto del artista de Radio · En directo (`NowPlayingFoto`).
//
// Probado con los siete conciertos del feed del 21.10.26: mueve Digitalism,
// Vida, Fontaines D.C. y MONO; Band of Horses y Muse casi nada; Chameleons,
// nada.
//
// Se mira la foto en pequeño, a 64 de ancho, y se recorre fila a fila desde
// cada borde: una fila es «fondo» mientras es **suave**, sin saltos entre un
// píxel y el de al lado. No se compara con un color fijo: la pared blanca de
// Digitalism va de 597 a 733 en una misma fila por el viñeteado, y eso es
// fondo; el pelo que empieza debajo salta más de 300 de un píxel al siguiente. Necesita que el servidor deje leer los píxeles
// (`access-control-allow-origin`); las de `/conciertos/images/` lo dejan. Si no,
// o si algo falla, devuelve `null` y la foto se queda como estaba.

const ANCHO = 64;
// El salto máximo entre dos píxeles vecinos de una fila de fondo, en la suma
// de los tres canales (0..765). Aguanta el grano de JPEG y un degradado; un
// borde de algo, no.
const SALTO = 60;

const cache = new Map();

function esFondo(datos, ancho, fila) {
  for (let x = 1; x < ancho; x += 1) {
    const i = (fila * ancho + x) * 4;
    const j = i - 4;
    const d =
      Math.abs(datos[i] - datos[j]) + Math.abs(datos[i + 1] - datos[j + 1]) + Math.abs(datos[i + 2] - datos[j + 2]);
    if (d > SALTO) return false;
  }
  return true;
}

function medir(img) {
  const alto = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * ANCHO));
  const lienzo = document.createElement("canvas");
  lienzo.width = ANCHO;
  lienzo.height = alto;
  const ctx = lienzo.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, ANCHO, alto);
  const { data } = ctx.getImageData(0, 0, ANCHO, alto);

  let arriba = 0;
  while (arriba < alto && esFondo(data, ANCHO, arriba)) arriba += 1;

  let abajo = 0;
  while (abajo < alto - arriba && esFondo(data, ANCHO, alto - 1 - abajo)) abajo += 1;

  // Toda la foto lisa: no hay nada que centrar.
  if (arriba + abajo >= alto) return null;

  return {
    // En fracción del alto de la foto.
    arriba: arriba / alto,
    abajo: abajo / alto,
    proporcion: img.naturalWidth / img.naturalHeight,
  };
}

/**
 * @param {string} url
 * @returns {Promise<{arriba: number, abajo: number, proporcion: number} | null>}
 */
export function medirMargenes(url) {
  if (!url || typeof document === "undefined") return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);

  const promesa = new Promise((resolver) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      try {
        resolver(medir(img));
      } catch (e) {
        // Sin permiso para leer los píxeles, o un lienzo que no se deja.
        resolver(null);
      }
    };
    img.onerror = () => resolver(null);
    img.src = url;
  });

  cache.set(url, promesa);
  return promesa;
}

/**
 * Dónde anclar en vertical una foto que cubre un visor de `anchoVisor ×
 * altoVisor` para que su contenido quede centrado, como `object-position` Y en
 * porcentaje. `null` si no hay margen que aprovechar: quien llama deja su
 * anclaje de siempre.
 */
export function anclajeVertical(margenes, anchoVisor, altoVisor) {
  if (!margenes || !anchoVisor || !altoVisor) return null;
  // Sin margen apreciable arriba, nada que corregir.
  if (margenes.arriba < 0.03) return null;

  const altoFoto = anchoVisor / margenes.proporcion;
  const sobra = altoFoto - altoVisor;
  if (sobra <= 1) return null;

  const centro = ((margenes.arriba + (1 - margenes.abajo)) / 2) * altoFoto;
  // 🔴 Nunca se come el contenido: se sube como mucho hasta dejar un respiro
  // del 8 % del visor sobre lo primero que hay. Sin este tope, una foto cuyos
  // cuerpos llegan al pie (Digitalism) se centraba cortando las cabezas.
  const tope = margenes.arriba * altoFoto - altoVisor * 0.08;
  const desplazamiento = Math.min(sobra, tope, Math.max(0, centro - altoVisor / 2));
  if (desplazamiento <= 1) return null;
  return (desplazamiento / sobra) * 100;
}
